import * as React from 'react';
import { ReactElement, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatMessageBundle, nativeDateToLocalDateAtUtc } from '@unifocus/js-utils';
import { ReactCalendar } from '../../../controls/reactcalendar';
import { convert, LocalDate } from '@js-joda/core';
import { Button } from '../../../controls/button';
import messages from '../../../../localized-messages';
import { ViewCallbackProperties } from 'react-calendar';

export interface DatePickerProps {
   onOk: (selectedDate: LocalDate | undefined) => void;
   onCancel: () => void;
   value?: LocalDate | null;
   minDate?: LocalDate;
   maxDate?: LocalDate;
   showTodayButton?: boolean;
   formatCell?: (date: LocalDate) => ReactElement | null;
}

export interface LocalDatePickerState {
   currentValue: LocalDate;
}

export const DatePicker = (props: DatePickerProps) => {
   const value = props.value;
   const calendarRef = useRef();
   const [currentValue, setCurrentValue] = useState<LocalDate | undefined>(value ? value : undefined);

   const initialValueRef = useRef(currentValue);

   const {
      minDate,
      maxDate,
      onOk,
      onCancel,
      showTodayButton,
      formatCell
   } = props;

   useEffect(() => {
      setCurrentValue(value || undefined);
      initialValueRef.current = value || undefined;
   }, [value]);

   const optionalProps = useMemo(() => Object.assign(
      {},
      minDate && { minDate: convert(minDate).toDate() },
      maxDate && { maxDate: convert(maxDate).toDate() },
      formatCell && {
            tileContent: ({ date }: { date: Date }) => {
               return formatCell ? formatCell(convertDateToLocalDateAtUtc(date)) : null;
            }
         })
      ,[ formatCell, maxDate, minDate ]);

   const onChange = useCallback((date: Date | Date[]): void => {
      setCurrentValue(convertDateToLocalDateAtUtc(date as Date));
   },[]);

   const onToday = useCallback((): void => {
      const today: LocalDate = LocalDate.now();

      navigateToCurrentDate(calendarRef, today);
      setCurrentValue(today);
   },[]);

   const onOkHandler = useCallback(() => {
      initialValueRef.current = currentValue;
      onOk(currentValue);
   }, [onOk, currentValue]);

   const onCancelHandler = useCallback(() => {
      setCurrentValue(initialValueRef.current);
      onCancel();
   }, [onCancel]);

   const calendarDate = currentValue ? convert(currentValue).toDate() : undefined;
   const [ activeStartDate, setActiveStartDate ] = useState<Date | undefined>(getActiveStartDate(currentValue, minDate, maxDate));

   useEffect(()=>{
      setActiveStartDate(getActiveStartDate(currentValue, minDate, maxDate));
   },[currentValue, minDate, maxDate]);

   const onActiveStartDateChange = useCallback((viewCallbackProperties: ViewCallbackProperties) => {
      setActiveStartDate(viewCallbackProperties.activeStartDate);
   },[]);

   const onKeyUpHandler = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
      if (e.keyCode === 13) {
         onOkHandler();
      }
   },[onOkHandler]);

   return (
      <div className={'uf-date-picker'} onKeyUp={onKeyUpHandler}>
         <ReactCalendar
            ref={calendarRef}
            activeStartDate={activeStartDate}
            value={calendarDate}
            onChange={onChange}
            onActiveStartDateChange={onActiveStartDateChange}
            {...optionalProps}
         />
         <DatePickerFooter
            onOk={onOkHandler}
            onCancel={onCancelHandler}
            onToday={showTodayButton !== false ? onToday : undefined}
         />
      </div>
   );
};

const getActiveStartDate = (startDate: LocalDate | undefined, minDate: LocalDate | undefined, maxDate: LocalDate | undefined) => {

   if (maxDate && startDate && startDate.isAfter(maxDate)) {
      return convert(maxDate.minusMonths(1).withDayOfMonth(1).atStartOfDay()).toDate();
   }

   if (minDate && (startDate === undefined || startDate.isBefore(minDate))) {
      return convert(minDate.withDayOfMonth(1).atStartOfDay()).toDate();
   }

   return startDate ? convert(startDate.withDayOfMonth(1).atStartOfDay()).toDate() : undefined;
};

const convertDateToLocalDateAtUtc = (date: Date): LocalDate =>
   nativeDateToLocalDateAtUtc(new Date(Date.UTC(date.getFullYear(),
                                                date.getMonth(),
                                                date.getDate())
   ));

const navigateToCurrentDate = (calendarRef: RefObject<any>, today: LocalDate) => {

   calendarRef.current?.setActiveStartDate(getFirstDayOfCurrentMonthAsNativeDate(today),
                                                  'month');
};

const getFirstDayOfCurrentMonthAsNativeDate = (today: LocalDate): Date => {
   return convert(today.withDayOfMonth(1)).toDate();
};

interface DatePickerFooterProps {
   onOk: () => void;
   onCancel: () => void;
   onToday?: () => void;
}

const DatePickerFooter = (props: DatePickerFooterProps): ReactElement => {

   const { onToday } = props;
   const onClickHandler = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      if (onToday) {
         onToday();
      }
   },[onToday]);

   return (
      <div className={'uf-date-picker-footer'}>
         {props.onToday &&
          <Button
             type={'link'}
             onClick={onClickHandler}
             href={'#'}
          >
             {formatMessageBundle(messages, 'res_today')}
          </Button>}

         <div>
            <Button
               size={'small'}
               onClick={props.onCancel}
            >
               {formatMessageBundle(messages, 'res_cancel')}
            </Button>

            <Button
               size={'small'}
               type="primary"
               onClick={props.onOk}
            >
               {formatMessageBundle(messages, 'res_ok')}
            </Button>
         </div>
      </div>
   );
};
